import { MessageOutlined } from '@ant-design/icons'
import { Breadcrumb } from 'antd'
import React, { useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router'
import styled from 'styled-components'
import { Divider } from 'sutra/components/Divider/Divider'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'

import { pageBackgroundGrey } from 'assets/styles/variables'
import { handleException } from 'clients/exception-handling'
import { KeyboardShortcutKeyAnnotation } from 'components/KeyboardShortcutKeyAnnotation'
import { ALERTS_URL } from 'features/alerts/paths'
import { ReportBug } from 'features/bug-report'
import { getCatalogLensFromCollectionType } from 'features/catalog'
import { LENS_LABEL } from 'features/catalog/constants'
import { CATALOG_URL } from 'features/catalog/paths'
import {
  NEW_SOCIAL_TOPIC_SEGMENT,
  SOCIAL_TOPICS_SEGMENT,
} from 'features/coverage/features/social-topics/paths'
import { COVERAGE_URL } from 'features/coverage/paths'
import {
  DASHBOARD_EDITOR_PATH_SEGMENT,
  DASHBOARD_URL,
} from 'features/dashboard/paths'
import type { FilterUIState } from 'features/filters/store'
import { useFilterUIStore } from 'features/filters/store'
import { FilterOption, FilterOptionSelect } from 'features/filters/types'
import { NotificationsContainer } from 'features/notifications'
import { useFeatureFlags } from 'features/project'
import { CommandPaletteContainer } from 'features/project/features/command-palette/Container'
import {
  getCatalogSubthemeNameFromBackend,
  parseThemeFilterValue,
} from 'features/project/partials/project-layout/catalog-breadcrumb-utils'
import AskYogiSvgIcon from 'features/project/partials/project-layout/components/navigation/components/AskYogiSvgIcon'
import { NavAnnotationPreview } from 'features/project/partials/project-layout/components/navigation/NavAnnotationPreview'
import { Navigation } from 'features/project/partials/project-layout/components/navigation/Navigation'
import { LayoutProvider } from 'features/project/partials/project-layout/LayoutContext'
import { routeNameMap } from 'features/project/partials/project-layout/route-name-map'
import {
  useAskYogiAnywhere,
  useNav,
  useTopBar,
} from 'features/project/partials/project-layout/use-layout'
import { useProjectStore } from 'features/project/store'
import {
  CONNECTOR_LIBRARY_URL,
  CONNECTORS_URL,
  SETTINGS_URL,
  SOURCE_INTEGRATIONS_URL,
} from 'features/settings/paths'
import { useAppAgentEvents } from 'features/trend-detection/hooks/use-app-agent-events'
import { AGENTS_URL, TREND_DETECTION_URL } from 'features/trend-detection/paths'
import { GLOBAL_SHORTCUT_KEYS } from 'hooks/keyboard-shortcuts'
import {
  useKeyboardShortcuts,
  useShortcutDisplay,
} from 'hooks/use-keyboard-shortcuts'
import { SCREEN_STATE_PAGES } from 'routes/builders'
import { DASHBOARDS_2_1_URL } from 'src/features/dashboards-v2-1/paths'
import { DASHBOARDS_2_URL } from 'src/features/dashboards-v2/paths'
import mixpanel from 'trackers/mixpanel'
import { ProjectState } from 'types/project'

interface CrispWindow {
  $crisp?: { push: (args: unknown[]) => void }
}

type StonlyWindow = Window & {
  StonlyWidget?: (...args: unknown[]) => unknown
}

type Props = {
  children: React.ReactNode
  isProjectLocked?: boolean
}

// Floating chat widget mounted on every project page. It reuses ask-yogi's
// Chart component, which pulls in the entire charts-v2 rendering stack
// (chart.js, recharts, every chart strategy) — a static import here would
// keep all of that in the eager bundle regardless of route-level splitting,
// since ProjectLayout wraps every route.
const AskYogiAnywhere = React.lazy(() =>
  import('features/ask-yogi-anywhere').then((mod) => ({
    default: mod.AskYogiAnywhere,
  })),
)

export const ProjectLayout: React.FC<Props> = ({
  children,
  isProjectLocked = false,
}) => {
  return (
    <LayoutProvider>
      <ProjectLayoutInner isProjectLocked={isProjectLocked}>
        {children}
      </ProjectLayoutInner>
    </LayoutProvider>
  )
}

const ProjectLayoutInner: React.FC<Props> = ({
  children,
  isProjectLocked = false,
}) => {
  const details = useProjectStore((state: ProjectState) => state.details)
  const projectId = details?.proj_uuid

  // One app-wide SSE connection drives both the live feed/agent-list query
  // invalidation and the run completion/failure toasts. Held here (not per
  // page) so a run that finishes after its launching tab closed still toasts
  // in any tab opened later. Locked projects open no stream and fire no toasts.
  useAppAgentEvents(isProjectLocked ? undefined : projectId)

  const { data: feature_flags } = useFeatureFlags()
  const {
    hasNestedSidebar,
    isNestedSidebarOpen,
    setPortalContainer,
    toggleMainNav,
    mainNavWidth,
    isMainNavCollapsed,
    setIsMainNavCollapsed,
  } = useNav()

  // hide_ask_yogi: This is to hide Ask Yogi for Disney. Delete this check
  // (and its two call sites below) once they approve Ask Yogi usage.
  const showAskYogi =
    !isProjectLocked &&
    !(feature_flags as Record<string, boolean> | undefined)?.['hide_ask_yogi']
  // The top-bar toggle button is sidebar-UI-only chrome — the legacy floating
  // UI opens via its own bottom-right button instead. The widget's own mount
  // point below stays unconditional on this flag; Container.tsx picks the
  // sidebar or legacy render tree internally. See
  // features/ask-yogi-anywhere/legacy/ for the flag-off fallback.
  const showAskYogiHeaderButton =
    showAskYogi &&
    Boolean(
      (feature_flags as Record<string, boolean> | undefined)?.[
        'dashboards_2_1'
      ],
    )

  // When collapsed, clicking the rail strip (not a nav item / button) expands it.
  const handleRailClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (!isMainNavCollapsed) return
    if ((e.target as HTMLElement).closest('a, button, [role="button"]')) return
    setIsMainNavCollapsed(false)
  }
  const { toggleAskYogiAnywhere } = useAskYogiAnywhere()
  const { pathname } = useLocation()
  const mainContentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    mainContentRef.current?.scrollTo({ top: 0 })
  }, [pathname])

  // Register global keyboard shortcuts
  useKeyboardShortcuts(
    {
      [GLOBAL_SHORTCUT_KEYS.MAIN_NAV_TOGGLE]: toggleMainNav,
      [GLOBAL_SHORTCUT_KEYS.ASK_YOGI_ANYWHERE_TOGGLE]: toggleAskYogiAnywhere,
    },
    { respectInputs: true, enabled: true },
  )

  useEffect(() => {
    if ((window as CrispWindow).$crisp) {
      ;(window as CrispWindow).$crisp?.push(['do', 'chat:hide'])
    }
  }, [])
  ;(window as CrispWindow).$crisp?.push([
    'on',
    'chat:closed',
    () => {
      ;(window as CrispWindow).$crisp?.push(['do', 'chat:hide'])
    },
  ])

  // Data is guaranteed by ProjectRoot's single bootstrap gate — no inner loader needed.
  return (
    <Wrapper>
      <NavBar
        width={mainNavWidth}
        $isCollapsed={isMainNavCollapsed}
        onClick={handleRailClick}
      >
        <Navigation isProjectLocked={isProjectLocked} />
        <NavAnnotationPreview />
      </NavBar>
      <Content $navWidth={mainNavWidth}>
        <TopNavBar showAskYogi={showAskYogiHeaderButton} />
        <ContentArea>
          {hasNestedSidebar && (
            <NestedSidebar $isOpen={isNestedSidebarOpen}>
              <div
                ref={setPortalContainer}
                style={{ height: '100%', width: '100%' }}
              />
            </NestedSidebar>
          )}
          <MainContent
            ref={mainContentRef}
            $hasNestedSidebar={hasNestedSidebar}
          >
            {children}
          </MainContent>

          {/* Ask Yogi Anywhere — docked sidebar, sibling of MainContent so
              it pushes page content aside instead of floating over it. */}
          {showAskYogi && (
            <React.Suspense fallback={null}>
              <AskYogiAnywhere />
            </React.Suspense>
          )}
        </ContentArea>
      </Content>

      <CommandPaletteContainer isProjectLocked={isProjectLocked} />
    </Wrapper>
  )
}

const safeDecodeURIComponent = (value: string): string => {
  try {
    return decodeURIComponent(value)
  } catch (cause) {
    handleException(cause, {
      operation: 'project.url.decode',
      disposition: 'expected',
      isExpectedError: (cause) => cause instanceof URIError,
      reason: 'Malformed URL encoding is displayed as its original text.',
    })

    return value
  }
}

const toHumanReadable = (value: string): string => {
  return safeDecodeURIComponent(value)
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

const getCatalogCollectionName = (
  lens: string | null,
  defaultFilterList: FilterOption[],
): string => {
  if (!lens) {
    return 'Catalog'
  }

  const filterOption = defaultFilterList.find(
    (option): option is FilterOptionSelect =>
      'values' in option && option.field === lens,
  )
  const alias = filterOption?.alias

  if (alias === 'Source Type') {
    return 'Sources'
  }
  if (alias) {
    return alias.endsWith('s') ? alias : `${alias}s`
  }

  return LENS_LABEL[lens] ?? toHumanReadable(lens)
}

const getCatalogItemNameFromBackend = (
  lens: string | null,
  itemName: string,
  defaultFilterList: FilterOption[],
): string => {
  const decodedItemName = safeDecodeURIComponent(itemName).trim()
  if (!lens) {
    return decodedItemName
  }

  const filterOption = defaultFilterList.find(
    (option): option is FilterOptionSelect =>
      'values' in option && option.field === lens,
  )
  // Title-case helper for fallback paths: "amazon · australia" → "Amazon · Australia".
  // Uses word-boundary capitalization so separators (·, hyphens) are preserved, unlike
  // toHumanReadable which collapses hyphens/underscores to spaces.
  const titleCase = (value: string): string =>
    value.replace(/\b\w/g, (char) => char.toUpperCase())

  if (!filterOption) {
    return titleCase(decodedItemName)
  }

  const matchedValue = filterOption.values.find(
    (value) => value.value.toLowerCase() === decodedItemName.toLowerCase(),
  )
  if (matchedValue) {
    return matchedValue.value
  }

  if (lens === 'theme') {
    const matchedThemeParent = filterOption.values.find((value) => {
      const parsed = parseThemeFilterValue(value.value)
      if (!parsed) {
        return false
      }
      return parsed.theme.toLowerCase() === decodedItemName.toLowerCase()
    })
    if (matchedThemeParent) {
      return (
        parseThemeFilterValue(matchedThemeParent.value)?.theme ??
        decodedItemName
      )
    }
  }

  return titleCase(decodedItemName)
}

const TopNavBar = ({ showAskYogi }: { showAskYogi: boolean }): JSX.Element => {
  const location = useLocation()
  const details = useProjectStore((state: ProjectState) => state.details)
  const defaultFilterList = useFilterUIStore(
    (state: FilterUIState) => state.defaultFilterList,
  )
  const { topNavActions, breadcrumbSubtitle } = useTopBar()
  const { isAskYogiAnywhereOpen, toggleAskYogiAnywhere } = useAskYogiAnywhere()
  const askYogiShortcut = useShortcutDisplay(
    GLOBAL_SHORTCUT_KEYS.ASK_YOGI_ANYWHERE_TOGGLE,
  )
  const projectId = details?.proj_uuid

  const handleAskYogiToggle = (): void => {
    if (!isAskYogiAnywhereOpen) {
      const pageSegment = location.pathname.split('/').filter(Boolean)[2]
      mixpanel.track('askyogi', {
        action: 'open_floating_chat',
        interface: 'floating_chat',
        ui: 'sidebar',
        focused_page: pageSegment ?? 'home',
      })
    }
    toggleAskYogiAnywhere()
  }

  // Parse the current path to generate breadcrumb items
  const getBreadcrumbItems = (): Array<{
    title: React.ReactNode
    onClick?: () => void
  }> => {
    const pathParts = location.pathname
      .split('/')
      .filter((part) => part.length > 0)

    // Build breadcrumb items array
    const items: Array<{ title: React.ReactNode; onClick?: () => void }> = []

    // First item - Home
    if (pathParts.length > 1) {
      const homeUrl = `/project/${pathParts[1]}`
      items.push({
        title: <BreadcrumbLink to={homeUrl}>Home</BreadcrumbLink>,
      })
    }

    // Determine the current page for breadcrumb display
    if (pathParts.length > 2) {
      if (pathParts[2] === CATALOG_URL && pathParts.length === 3) {
        const defaultCollectionName = getCatalogCollectionName(
          'product_hierarchy',
          defaultFilterList,
        )
        const catalogUrl = `/project/${pathParts[1]}/${CATALOG_URL}`

        items.push({
          title: (
            <BreadcrumbLink to={catalogUrl}>
              {routeNameMap[CATALOG_URL] || 'Catalog'}
            </BreadcrumbLink>
          ),
        })
        items.push({
          title: <CurrentBreadcrumb>{defaultCollectionName}</CurrentBreadcrumb>,
        })

        return items
      }

      if (pathParts[2] === CATALOG_URL && pathParts.length > 3) {
        const collectionType = pathParts[3]
        const lens = getCatalogLensFromCollectionType(collectionType)
        const collectionName = getCatalogCollectionName(lens, defaultFilterList)
        const catalogUrl = `/project/${pathParts[1]}/${CATALOG_URL}`
        const collectionUrl = `/project/${pathParts[1]}/${CATALOG_URL}/${collectionType}`
        const itemName = pathParts[4]
        const subthemeName = pathParts[5] === 'subthemes' ? pathParts[6] : null

        items.push({
          title: (
            <BreadcrumbLink to={catalogUrl}>
              {routeNameMap[CATALOG_URL] || 'Catalog'}
            </BreadcrumbLink>
          ),
        })

        if (itemName) {
          const catalogItemName = getCatalogItemNameFromBackend(
            lens,
            itemName,
            defaultFilterList,
          )
          const itemUrl = `/project/${pathParts[1]}/${CATALOG_URL}/${collectionType}/${itemName}`
          items.push({
            title: (
              <BreadcrumbLink to={collectionUrl}>
                {collectionName}
              </BreadcrumbLink>
            ),
          })

          if (subthemeName && lens === 'theme') {
            const catalogSubthemeName = getCatalogSubthemeNameFromBackend(
              catalogItemName,
              subthemeName,
              defaultFilterList,
            )
            items.push({
              title: (
                <Tooltip content={catalogItemName} side="bottom">
                  <BreadcrumbLink to={itemUrl}>
                    <LinkedTruncatedBreadcrumb>
                      {catalogItemName}
                    </LinkedTruncatedBreadcrumb>
                  </BreadcrumbLink>
                </Tooltip>
              ),
            })
            items.push({
              title: (
                <Tooltip content={catalogSubthemeName} side="bottom">
                  <TruncatedBreadcrumb>
                    {catalogSubthemeName}
                  </TruncatedBreadcrumb>
                </Tooltip>
              ),
            })
          } else {
            items.push({
              title: (
                <Tooltip content={catalogItemName} side="bottom">
                  <TruncatedBreadcrumb>{catalogItemName}</TruncatedBreadcrumb>
                </Tooltip>
              ),
            })
          }
        } else {
          items.push({
            title: <CurrentBreadcrumb>{collectionName}</CurrentBreadcrumb>,
          })
        }

        return items
      }

      // Dashboard detail or edit: /project/:id/custom-dashboards/:dashboardId or .../edit
      if (
        pathParts[2] === DASHBOARD_URL &&
        pathParts.length >= 4 &&
        pathParts[3] !== 'new'
      ) {
        const dashboardsUrl = `/project/${pathParts[1]}/${DASHBOARD_URL}`
        const isEdit = pathParts[4] === DASHBOARD_EDITOR_PATH_SEGMENT
        const dashboardViewUrl = `/project/${pathParts[1]}/${DASHBOARD_URL}/${pathParts[3]}`

        items.push({
          title: (
            <BreadcrumbLink to={dashboardsUrl}>
              {routeNameMap[DASHBOARD_URL] || 'Dashboards'}
            </BreadcrumbLink>
          ),
        })
        if (breadcrumbSubtitle) {
          if (isEdit) {
            items.push({
              title: (
                <Tooltip content={breadcrumbSubtitle} side="bottom">
                  <BreadcrumbLink to={dashboardViewUrl}>
                    <LinkedTruncatedBreadcrumb>
                      {breadcrumbSubtitle}
                    </LinkedTruncatedBreadcrumb>
                  </BreadcrumbLink>
                </Tooltip>
              ),
            })
            items.push({
              title: <CurrentBreadcrumb>Edit</CurrentBreadcrumb>,
            })
          } else {
            items.push({
              title: (
                <Tooltip content={breadcrumbSubtitle} side="bottom">
                  <TruncatedBreadcrumb>
                    {breadcrumbSubtitle}
                  </TruncatedBreadcrumb>
                </Tooltip>
              ),
            })
          }
        }
        return items
      }

      // Dashboard 2.0/2.1 detail or edit: /project/:id/dashboards/:dashboardId
      // (2.0 still has a separate .../edit route) or
      // /project/:id/dashboards-2-1/:dashboardId (2.1's single role-gated
      // route — there is no separate .../edit route to detect here any more).
      const dashboardsUrlSegment =
        pathParts[2] === DASHBOARDS_2_URL
          ? DASHBOARDS_2_URL
          : pathParts[2] === DASHBOARDS_2_1_URL
            ? DASHBOARDS_2_1_URL
            : undefined
      const isDashboards2 = dashboardsUrlSegment !== undefined
      if (isDashboards2 && pathParts.length >= 4 && pathParts[3] !== 'new') {
        const dashboardsUrl = `/project/${pathParts[1]}/${dashboardsUrlSegment}`
        const isEdit =
          dashboardsUrlSegment === DASHBOARDS_2_URL && pathParts[4] === 'edit'
        const dashboardViewUrl = `/project/${pathParts[1]}/${dashboardsUrlSegment}/${pathParts[3]}`

        items.push({
          title: (
            <BreadcrumbLink to={dashboardsUrl}>
              {routeNameMap[dashboardsUrlSegment] || 'Dashboards 2.0'}
            </BreadcrumbLink>
          ),
        })
        if (breadcrumbSubtitle) {
          if (isEdit) {
            items.push({
              title: (
                <Tooltip content={breadcrumbSubtitle} side="bottom">
                  <BreadcrumbLink to={dashboardViewUrl}>
                    <LinkedTruncatedBreadcrumb>
                      {breadcrumbSubtitle}
                    </LinkedTruncatedBreadcrumb>
                  </BreadcrumbLink>
                </Tooltip>
              ),
            })
            items.push({
              title: <CurrentBreadcrumb>Edit</CurrentBreadcrumb>,
            })
          } else {
            items.push({
              title: (
                <Tooltip content={breadcrumbSubtitle} side="bottom">
                  <TruncatedBreadcrumb>
                    {breadcrumbSubtitle}
                  </TruncatedBreadcrumb>
                </Tooltip>
              ),
            })
          }
        }
        return items
      }

      // Insights feed: feed, issue/update detail, benchmarking insights
      if (pathParts[2] === TREND_DETECTION_URL) {
        const feedUrl = `/project/${pathParts[1]}/${TREND_DETECTION_URL}`

        if (pathParts.length === 3) {
          items.push({
            title: <CurrentBreadcrumb>Insights</CurrentBreadcrumb>,
          })
          return items
        }

        items.push({
          title: <BreadcrumbLink to={feedUrl}>Insights</BreadcrumbLink>,
        })

        if (
          pathParts[3] === 'issues' ||
          pathParts[3] === 'updates' ||
          pathParts[3] === 'benchmarking-insights'
        ) {
          if (breadcrumbSubtitle) {
            items.push({
              title: (
                <Tooltip content={breadcrumbSubtitle} side="bottom">
                  <TruncatedBreadcrumb>
                    {breadcrumbSubtitle}
                  </TruncatedBreadcrumb>
                </Tooltip>
              ),
            })
          }
          return items
        }
      }

      // Coverage: the page, and a social topic's (new or named) under it
      if (pathParts[2] === COVERAGE_URL) {
        const coverageUrl = `/project/${pathParts[1]}/${COVERAGE_URL}`

        if (pathParts.length === 3) {
          items.push({
            title: <CurrentBreadcrumb>Coverage</CurrentBreadcrumb>,
          })
          return items
        }

        items.push({
          title: <BreadcrumbLink to={coverageUrl}>Coverage</BreadcrumbLink>,
        })

        if (pathParts[3] === SOCIAL_TOPICS_SEGMENT) {
          if (pathParts[4] === NEW_SOCIAL_TOPIC_SEGMENT) {
            items.push({
              title: <CurrentBreadcrumb>New topic</CurrentBreadcrumb>,
            })
          } else if (breadcrumbSubtitle) {
            // The topic's title, set by its page once loaded; never the uuid.
            items.push({
              title: (
                <Tooltip content={breadcrumbSubtitle} side="bottom">
                  <TruncatedBreadcrumb>
                    {breadcrumbSubtitle}
                  </TruncatedBreadcrumb>
                </Tooltip>
              ),
            })
          }
          return items
        }
      }

      // Agents: list, create, agent config, benchmarking agent
      if (pathParts[2] === AGENTS_URL) {
        const agentsUrl = `/project/${pathParts[1]}/${AGENTS_URL}`

        if (pathParts.length === 3) {
          items.push({
            title: <CurrentBreadcrumb>Agents</CurrentBreadcrumb>,
          })
          return items
        }

        items.push({
          title: <BreadcrumbLink to={agentsUrl}>Agents</BreadcrumbLink>,
        })

        if (pathParts[3] === 'create') {
          items.push({
            title: <CurrentBreadcrumb>Create agent</CurrentBreadcrumb>,
          })
          return items
        }

        // /agents/:agentId or /agents/benchmarking/:agentId — show subtitle if available
        if (breadcrumbSubtitle) {
          items.push({
            title: (
              <Tooltip content={breadcrumbSubtitle} side="bottom">
                <TruncatedBreadcrumb>{breadcrumbSubtitle}</TruncatedBreadcrumb>
              </Tooltip>
            ),
          })
        } else {
          items.push({
            title: <CurrentBreadcrumb>Agent</CurrentBreadcrumb>,
          })
        }
        return items
      }

      // Settings → Source Integrations → {source}
      if (
        pathParts[2] === SETTINGS_URL &&
        pathParts[3] === SOURCE_INTEGRATIONS_URL
      ) {
        const settingsUrl = `/project/${pathParts[1]}/${SETTINGS_URL}`
        const sourcesUrl = `${settingsUrl}/${SOURCE_INTEGRATIONS_URL}`
        const sourcesLabel = routeNameMap[SOURCE_INTEGRATIONS_URL]
        items.push({
          title: (
            <BreadcrumbLink to={settingsUrl}>
              {routeNameMap[SETTINGS_URL] || 'Settings'}
            </BreadcrumbLink>
          ),
        })
        if (pathParts.length === 4) {
          items.push({
            title: <CurrentBreadcrumb>{sourcesLabel}</CurrentBreadcrumb>,
          })
          return items
        }
        // The add / edit screen (…/<segment>/new | …/<segment>/:uuid).
        items.push({
          title: (
            <BreadcrumbLink to={sourcesUrl}>{sourcesLabel}</BreadcrumbLink>
          ),
        })
        // The container sets the subtitle to the source's name; the path
        // segment is a uuid, so there is nothing readable to fall back to
        // until it arrives.
        const leaf = breadcrumbSubtitle || 'Source'
        items.push({
          title: (
            <Tooltip content={leaf} side="bottom">
              <TruncatedBreadcrumb>{leaf}</TruncatedBreadcrumb>
            </Tooltip>
          ),
        })
        return items
      }

      // Settings → Connectors → {connector}
      if (pathParts[2] === SETTINGS_URL && pathParts[3] === CONNECTORS_URL) {
        const settingsUrl = `/project/${pathParts[1]}/${SETTINGS_URL}`
        const connectorsUrl = `${settingsUrl}/${CONNECTORS_URL}`
        items.push({
          title: (
            <BreadcrumbLink to={settingsUrl}>
              {routeNameMap[SETTINGS_URL] || 'Settings'}
            </BreadcrumbLink>
          ),
        })
        if (pathParts.length === 4) {
          items.push({
            title: (
              <CurrentBreadcrumb>
                {routeNameMap[CONNECTORS_URL] || 'Connectors'}
              </CurrentBreadcrumb>
            ),
          })
          return items
        }
        // detail (…/connectors/:slug[/callback])
        items.push({
          title: (
            <BreadcrumbLink to={connectorsUrl}>
              {routeNameMap[CONNECTORS_URL] || 'Connectors'}
            </BreadcrumbLink>
          ),
        })
        const leaf = breadcrumbSubtitle || toHumanReadable(pathParts[4]!)
        items.push({
          title: (
            <Tooltip content={leaf} side="bottom">
              <TruncatedBreadcrumb>{leaf}</TruncatedBreadcrumb>
            </Tooltip>
          ),
        })
        return items
      }

      // Settings → Data connections → {connector}
      //
      // Needs its own branch for the same reason the connectors tree above does:
      // without one the generic tail emits the raw path segment, so a connector
      // showed as "GorgiasConnector" — the SourceModel.source token the URL
      // carries — and the subtitle appended a second crumb beside it. The leaf
      // prefers the subtitle the detail page sets, since no humanising of that
      // token yields the connector's display name.
      if (
        pathParts[2] === SETTINGS_URL &&
        pathParts[3] === CONNECTOR_LIBRARY_URL
      ) {
        const settingsUrl = `/project/${pathParts[1]}/${SETTINGS_URL}`
        const libraryUrl = `${settingsUrl}/${CONNECTOR_LIBRARY_URL}`
        items.push({
          title: (
            <BreadcrumbLink to={settingsUrl}>
              {routeNameMap[SETTINGS_URL] || 'Settings'}
            </BreadcrumbLink>
          ),
        })
        const libraryLabel =
          routeNameMap[CONNECTOR_LIBRARY_URL] || 'Data connections'
        if (pathParts.length === 4) {
          items.push({
            title: <CurrentBreadcrumb>{libraryLabel}</CurrentBreadcrumb>,
          })
          return items
        }
        items.push({
          title: (
            <BreadcrumbLink to={libraryUrl}>{libraryLabel}</BreadcrumbLink>
          ),
        })
        const connectorLeaf =
          breadcrumbSubtitle || toHumanReadable(pathParts[4]!)
        // The credential form lives at its own URL, so it gets its own crumb —
        // which is what turns the connector into a LINK back up. Without it the
        // form's trail ended on a non-link connector crumb, offering no way back
        // and reading the same as the connector's own page.
        const formAction =
          pathParts[5] === 'new'
            ? 'Add connection'
            : pathParts[7] === 'replace'
              ? 'Replace credential'
              : null
        if (formAction === null) {
          items.push({
            title: (
              <Tooltip content={connectorLeaf} side="bottom">
                <TruncatedBreadcrumb>{connectorLeaf}</TruncatedBreadcrumb>
              </Tooltip>
            ),
          })
          return items
        }
        items.push({
          title: (
            <Tooltip content={connectorLeaf} side="bottom">
              <BreadcrumbLink to={`${libraryUrl}/${pathParts[4]}`}>
                <LinkedTruncatedBreadcrumb>
                  {connectorLeaf}
                </LinkedTruncatedBreadcrumb>
              </BreadcrumbLink>
            </Tooltip>
          ),
        })
        items.push({
          title: <CurrentBreadcrumb>{formAction}</CurrentBreadcrumb>,
        })
        return items
      }

      if (pathParts[2] === ALERTS_URL && pathParts.length > 3) {
        const alertsUrl = `/project/${pathParts[1]}/${ALERTS_URL}`
        items.push({
          title: (
            <BreadcrumbLink to={alertsUrl}>
              {routeNameMap[ALERTS_URL] || 'Alerts'}
            </BreadcrumbLink>
          ),
        })

        if (breadcrumbSubtitle) {
          items.push({
            title: (
              <Tooltip content={breadcrumbSubtitle} side="bottom">
                <TruncatedBreadcrumb>{breadcrumbSubtitle}</TruncatedBreadcrumb>
              </Tooltip>
            ),
          })
        }

        return items
      }

      const secondToLastPart =
        pathParts.length > 3 ? pathParts[pathParts.length - 2] : null
      const lastPart = pathParts[pathParts.length - 1]

      // FILTER PAGE HASH DETECTION:
      // URLs like /feedback/{hash} have 4+ segments. The breadcrumb's normal
      // subroute logic would treat this as "Feedback > {hash}" (like how
      // /insights/{insightId} shows "Insights > insight title").
      //
      // But filter hashes are NOT meaningful subroutes — they're opaque MD5
      // strings that shouldn't appear in the breadcrumb. So we detect when
      // the second-to-last part is a SCREEN_STATE_PAGES member and treat the whole
      // thing as a flat page (just show "Feedback", not "Feedback > abc123").
      const isFilterPageHash =
        secondToLastPart && SCREEN_STATE_PAGES.has(secondToLastPart)

      // True subroutes: /insights/{insightId} where insights is in routeNameMap
      // and is NOT a filter page. These get a two-level breadcrumb.
      const isOnSubroute =
        secondToLastPart && routeNameMap[secondToLastPart] && !isFilterPageHash

      if (isOnSubroute) {
        // Two-level breadcrumb: "Insights Feed > Insight Title"
        const routeName = routeNameMap[secondToLastPart]
        const parentUrl = `/project/${pathParts[1]}/${secondToLastPart}`

        items.push({
          title: <BreadcrumbLink to={parentUrl}>{routeName}</BreadcrumbLink>,
        })

        const subrouteLabel = breadcrumbSubtitle || toHumanReadable(lastPart!)
        items.push({
          title: (
            <Tooltip content={subrouteLabel} side="bottom">
              <TruncatedBreadcrumb>{subrouteLabel}</TruncatedBreadcrumb>
            </Tooltip>
          ),
        })
      } else {
        // Single-level breadcrumb: just the page name.
        // For filter pages with a hash (e.g. /feedback/{hash}), use the
        // page segment (secondToLastPart), not the hash (lastPart).
        const currentRoute = isFilterPageHash ? secondToLastPart : lastPart
        if (!currentRoute) return items
        const routeName =
          routeNameMap[currentRoute] || toHumanReadable(currentRoute)

        // If we have a breadcrumb subtitle (e.g., from query params like ?agent=uuid),
        // show the current route as clickable and add the subtitle
        if (breadcrumbSubtitle) {
          const currentUrl = `/project/${pathParts[1]}/${currentRoute}`
          items.push({
            title: <BreadcrumbLink to={currentUrl}>{routeName}</BreadcrumbLink>,
          })

          // Add the subtitle
          items.push({
            title: (
              <Tooltip content={breadcrumbSubtitle} side="bottom">
                <TruncatedBreadcrumb>{breadcrumbSubtitle}</TruncatedBreadcrumb>
              </Tooltip>
            ),
          })
        } else {
          // No subtitle - just show current route as final breadcrumb
          items.push({
            title: <CurrentBreadcrumb>{routeName}</CurrentBreadcrumb>,
          })
        }
      }
    }

    return items
  }

  return (
    <TopNavBarContainer>
      <TopNavLeft>
        {/* Page-specific actions */}
        {topNavActions && (
          <>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {topNavActions}
            </div>
            <Divider
              orientation="vertical"
              style={{ height: '24px', margin: 0 }}
            />
          </>
        )}

        <StyledBreadcrumb items={getBreadcrumbItems()} />
      </TopNavLeft>

      {/* Global actions */}
      <TopNavRight>
        {/*
          Bell rides inline here on project routes (LEFT of Report bug)
          instead of floating as a fixed overlay above the TopNavBar.
          ``SignedInLayout`` suppresses its overlay for ``/project/*``
          so we don't show two bells.
        */}
        <NotificationsContainer />
        <ReportBug projectId={projectId} />
        <HelpButton
          onClick={() => {
            mixpanel.track('help', {
              proj_uuid: projectId,
            })
            return (window as StonlyWindow).StonlyWidget?.('setYOffset', 0)
          }}
          className={'nav-help-button'}
        >
          <MessageOutlined />
          <span>Help</span>
        </HelpButton>
        {showAskYogi && (
          <Tooltip
            content={
              <>
                Ask Yogi{' '}
                <KeyboardShortcutKeyAnnotation shortcut={askYogiShortcut} />
              </>
            }
            side="bottom"
          >
            <AskYogiHeaderButton
              onClick={handleAskYogiToggle}
              $isActive={isAskYogiAnywhereOpen}
            >
              <AskYogiSvgIcon />
              <span>Ask Yogi</span>
            </AskYogiHeaderButton>
          </Tooltip>
        )}
      </TopNavRight>
    </TopNavBarContainer>
  )
}

const BreadcrumbLink = styled(Link)`
  color: #8c8c8c;
  cursor: pointer;
  text-decoration: none;
  &:hover {
    color: #595959;
    text-decoration: none;
  }
`

const CurrentBreadcrumb = styled.span`
  color: #000000;
  font-weight: 400;
  display: block;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const TruncatedBreadcrumb = styled.span`
  color: #000000;
  font-weight: 400;
  display: block;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

// The same truncated crumb, but inside a BreadcrumbLink: it takes the link's
// colour (including its hover) instead of the flat black a standalone leaf uses.
const LinkedTruncatedBreadcrumb = styled(TruncatedBreadcrumb)`
  color: inherit;
`

// Keeps the breadcrumb trail on a single line: antd v5 defaults the trail's
// <ol> to flex-wrap, which is what pushes it onto a second line once a long
// crumb doesn't fit. Only the long/user-controlled crumb (Truncated or
// Current) is allowed to shrink and clamp further with an ellipsis; the
// short static crumbs (Home, Dashboards 2.1, Edit, ...) stay full-width.
const StyledBreadcrumb = styled(Breadcrumb)`
  min-width: 0;

  ol {
    flex-wrap: nowrap;
    min-width: 0;
  }

  li {
    flex: 0 0 auto;
    white-space: nowrap;
  }

  li:has(${TruncatedBreadcrumb}), li:has(${CurrentBreadcrumb}) {
    flex: 0 1 auto;
    min-width: 0;
    max-width: 240px;
    overflow: hidden;
  }

  li:has(${TruncatedBreadcrumb}) a, li:has(${CurrentBreadcrumb}) a {
    display: block;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
  }
`

const TopNavBarContainer = styled.div`
  padding: 8px 24px;
  border-bottom: 1px solid #d8d8d8;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fcfafa;
`

const TopNavLeft = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  min-width: 0;
  flex: 1 1 auto;
  overflow: hidden;
`

const TopNavRight = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex: 0 0 auto;
`

const HelpButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 12px;
  min-width: 105px;
  background: transparent;
  border: 1px solid #e3e3e3;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 12px;
  color: #000;
  white-space: nowrap;

  &:hover {
    background: #5072a426;
    border-color: #d9d9d9;
  }
`

const AskYogiHeaderButton = styled.button<{ $isActive: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 14px;
  background: ${({ $isActive }) => ($isActive ? '#3e5a82' : '#4f72a4')};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
  font-size: 12px;
  font-weight: 500;
  color: white;
  white-space: nowrap;

  &:hover {
    background: #3e5a82;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`

const Wrapper = styled.div`
  //position: relative;
  overflow: hidden;
  display: flex;
  height: 100vh;
  background: ${pageBackgroundGrey};
`

const NavBar = styled.div<{ width: number; $isCollapsed?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  width: ${(props) => props.width}px;
  border-right: 1px solid #d8d8d8;
  cursor: ${({ $isCollapsed }) => ($isCollapsed ? 'pointer' : 'default')};
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`

const Content = styled.div<{ $navWidth: number }>`
  flex: 1;
  min-width: 0; /* Important for flex child to shrink */
  display: flex;
  flex-direction: column;
  height: 100vh;
`

const ContentArea = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`

const NestedSidebar = styled.div<{ $isOpen: boolean }>`
  width: ${(props) => (props.$isOpen ? '280px' : '50px')};
  border-right: 1px solid #d8d8d8;
  // background: white;
  overflow: hidden;
  flex-shrink: 0;
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  /* Only enable scrolling when fully open */
  ${(props) =>
    props.$isOpen &&
    `
    overflow-y: auto;
  `}
`

const MainContent = styled.div<{ $hasNestedSidebar: boolean }>`
  flex: 1;
  overflow-y: auto;
  min-width: 0;
`
